import React, { useState } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Button } from './ui/button';
import { Calendar, Phone, X, Sparkles, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Day7CoachingCTA({ show, calendlyLink, onDismiss }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [dismissing, setDismissing] = useState(false);

  if (!show) return null;

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.post('/welcome-challenge/dismiss-cta');
    } catch (e) {
      console.error('Dismiss CTA error:', e);
    }
    onDismiss?.();
  };

  const handleBook = () => {
    window.open(calendlyLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="day7-cta-overlay"
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0, y: 30 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="bg-card border border-border rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl"
          data-testid="day7-cta-modal"
        >
          {/* Gradient Header */}
          <div className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-6 pb-4 text-center">
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-secondary/60 transition-colors"
              data-testid="day7-cta-close"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>

            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center shadow-lg shadow-primary/25 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>

            <h2 className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-foreground">
              {fr ? 'Felicitations!' : 'Congratulations!'}
            </h2>
            <p className="text-primary font-heading text-sm mt-1 tracking-wider">
              {fr ? '7 JOURS COMPLETES' : '7 DAYS COMPLETED'}
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed text-center">
              {fr
                ? 'Pour t\'aider a maximiser tes resultats, reserve ton appel stratégique 100% GRATUIT avec un Coach.'
                : 'To help you maximize your results, book your 100% FREE strategic call with a Coach.'}
            </p>
            <p className="text-sm text-foreground leading-relaxed text-center font-medium">
              {fr
                ? 'Nous allons optimiser ton application ensemble et te creer ton premier plan d\'entraînement sur mesure.'
                : 'We\'ll optimize your app together and create your first custom training plan.'}
            </p>

            {/* Benefits */}
            <div className="space-y-2.5 pt-1">
              {[
                { icon: '🎯', text: fr ? 'Objectifs personnalises a TON profil' : 'Goals personalized to YOUR profile' },
                { icon: '💪', text: fr ? 'Plan d\'entraînement sur mesure' : 'Custom training plan' },
                { icon: '🍽', text: fr ? 'Optimisation de ta nutrition' : 'Nutrition optimization' },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-secondary/30">
                  <span className="text-lg">{b.icon}</span>
                  <span className="text-xs text-foreground">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="px-6 pb-6 space-y-2.5">
            <Button
              onClick={handleBook}
              className="w-full h-12 bg-gradient-to-r from-primary to-orange-600 hover:opacity-90 text-white font-heading uppercase tracking-wider text-sm shadow-lg shadow-primary/20"
              data-testid="day7-cta-book"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {fr ? 'Reserver mon appel GRATUIT' : 'Book my FREE call'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="w-full text-center text-[11px] text-muted-foreground/50 hover:text-muted-foreground transition-colors py-1"
              data-testid="day7-cta-dismiss"
            >
              {fr ? 'Plus tard' : 'Later'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
